import React, { useState, useEffect, useCallback, useRef } from 'react';
import { stationAPI, cityAPI } from '../services/api';
import { Station, City } from '../types';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  FormControlLabel,
  Collapse,
  Card,
  CardContent,
  Checkbox,
  Autocomplete
} from '@mui/material';
import { Edit, Delete, Refresh, FilterList, Clear, Add, Upload, Search, Download } from '@mui/icons-material';
import TranslationInput from '../components/TranslationInput';
import StationImportModal from '../components/StationImportModal';

// Define proper error type
interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const Stations: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([]);
  
  // Safety function to ensure stations is always an array
  const safeSetStations = (data: any) => {
    console.log('safeSetStations called with:', data);
    console.log('Data type:', typeof data);
    console.log('Is array:', Array.isArray(data));
    
    if (Array.isArray(data)) {
      console.log('Setting stations to array with length:', data.length);
      setStations(data);
    } else {
      console.error('Attempted to set stations with non-array data:', data);
      console.error('Data type:', typeof data);
      console.error('Data constructor:', data?.constructor?.name);
      setStations([]);
      setError('Invalid data format received from server');
    }
  };
  
  // Safety function to ensure cities is always an array
  const safeSetCities = (data: any) => {
    console.log('safeSetCities called with:', data);
    console.log('Data type:', typeof data);
    console.log('Is array:', Array.isArray(data));
    
    if (Array.isArray(data)) {
      console.log('Setting cities to array with length:', data.length);
      setCities(data);
    } else {
      console.error('Attempted to set cities with non-array data:', data);
      console.error('Data type:', typeof data);
      console.error('Data constructor:', data?.constructor?.name);
      setCities([]);
      setFormError('Invalid cities data format received from server');
    }
  };
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchingCities, setFetchingCities] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingStationId, setEditingStationId] = useState<string | null>(null);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [filterForm, setFilterForm] = useState({
    name: '',
    code: '',
    cityId: '', // Changed from 'city' to 'cityId' to match backend
    isInterchange: '',
    isActive: '',
    status: ''
  });

  // Simple filter model for API
  const getSimpleFilter = () => {
    const filter: { name?: string; code?: string; cityId?: string } = {};
    
    if (filterForm.cityId) {
      // Convert cityId to city name for API compatibility
      const city = cities.find(c => c.id === filterForm.cityId);
      if (city) {
        filter.cityId = city.id;
      }
    }
    
    if (filterForm.name) {
      filter.name = filterForm.name;
    }
    
    if (filterForm.code) {
      filter.code = filterForm.code;
    }
    
    return filter;
  };
  
  // Ref to store current filter values for debounced search
  const filterFormRef = useRef(filterForm);
  filterFormRef.current = filterForm;
  
  // Ref for debounce timeout
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [form, setForm] = useState({
    name: '',
    stationCode: '',
    cityId: '',
    isInterchange: false,
    lat: '',
    lng: '',
    description: '',
    firstTrainTime: '',
    lastTrainTime: '',
    isActive: true,
    status: 'OPERATIONAL',
    structures: [] as string[],
    totalPlatforms: '',
    totalTracks: ''
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    console.log('Stations component mounted');
    console.log('Initial stations state:', stations);
    console.log('Initial cities state:', cities);
    fetchStations();
    fetchCities();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Safety effect to ensure stations is always an array
  useEffect(() => {
    console.log('Safety effect triggered for stations:', stations);
    if (!Array.isArray(stations)) {
      console.error('Stations state is not an array, resetting to empty array');
      console.error('Stations type:', typeof stations);
      console.error('Stations value:', stations);
      setStations([]);
      setError('Data format error detected, please refresh the page');
    }
  }, [stations]);
  
  // Safety effect to ensure cities is always an array
  useEffect(() => {
    console.log('Safety effect triggered for cities:', cities);
    if (!Array.isArray(cities)) {
      console.error('Cities state is not an array, resetting to empty array');
      console.error('Cities type:', typeof cities);
      console.error('Cities value:', cities);
      setCities([]);
      setFormError('Cities data format error detected, please refresh the page');
    }
  }, [cities]);
  
  // Effect to handle filter changes and trigger search
  useEffect(() => {
    // Only trigger search if we have filters and we're not on the initial load
    if (Object.values(filterForm).some(value => value !== '') && !loading) {
      performSearch();
    }
  }, [filterForm.isInterchange, filterForm.isActive, filterForm.status]); // Only for dropdown filters

  const fetchStations = async () => {
    console.log('fetchStations called');
    setLoading(true);
    setError(null);
    try {
      console.log('Calling stationAPI.searchStations with simple filter...');
      const simpleFilter = getSimpleFilter();
      const res = await stationAPI.searchStations(simpleFilter, 0, pageSize); // Get all stations with large page size
      console.log('Stations API response:', res);
      console.log('Stations data:', res.data);
      console.log('Stations data type:', typeof res.data);
      console.log('Is array:', Array.isArray(res.data));
      console.log('Response status:', res.status);
      console.log('Response headers:', res.headers);
      console.log('Response data keys:', res.data ? Object.keys(res.data) : 'No data');
      
      // Check if we got a successful response
      if (res.status >= 200 && res.status < 300) {
        const pageResponse = res.data;
        safeSetStations(pageResponse.content);
        setTotalElements(pageResponse.totalElements);
        setTotalPages(pageResponse.totalPages);
        setCurrentPage(pageResponse.pageNumber);
        setPageSize(pageResponse.pageSize);
      } else {
        console.error('Unexpected response status:', res.status);
        setError(`Unexpected response status: ${res.status}`);
        setStations([]);
      }
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Error fetching stations:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      console.error('Error type:', typeof error);
      console.error('Error constructor:', error.constructor.name);
      
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to view stations.');
      } else {
        setError(error.response?.data?.message || error.message || 'Failed to load stations');
      }
      setStations([]);
    } finally {
      setLoading(false);
    }
  };



  const clearFilters = () => {
    setFilterForm({
      name: '',
      code: '',
      cityId: '',
      isInterchange: '',
      isActive: '',
      status: ''
    });
    setCurrentPage(0);
    setPageSize(20); // Reset to default page size
    fetchStations();
  };


  
  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchTerm: string) => {
      if (searchTerm.trim()) {
        setCurrentPage(0);
        await performSearch(0);
      } else {
        setCurrentPage(0);
        fetchStations();
      }
    }, 300),
    [] // Remove filterForm dependency to prevent recreation on every render
  );
  
  // Debounce utility function
  function debounce<T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }
  
  // Perform search using the new search API
  const performSearch = async (page?: number, size?: number) => {
    setSearchLoading(true);
    setError(null);
    try {
      const simpleFilter = getSimpleFilter();
      
      // Use provided page/size or current state values
      const searchPage = page !== undefined ? page : currentPage;
      const searchSize = size !== undefined ? size : pageSize;
      
      const res = await stationAPI.searchStations(simpleFilter, searchPage, searchSize);
      console.log('Search API response:', res);
      
      if (res.status >= 200 && res.status < 300) {
        const pageResponse = res.data;
        safeSetStations(pageResponse.content);
        setTotalElements(pageResponse.totalElements);
        setTotalPages(pageResponse.totalPages);
        setCurrentPage(pageResponse.pageNumber);
        setPageSize(pageResponse.pageSize);
      } else {
        console.error('Unexpected search response status:', res.status);
        setError(`Unexpected response status: ${res.status}`);
        setStations([]);
      }
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Error performing search:', error);
      setError(error.response?.data?.message || error.message || 'Failed to perform search');
      setStations([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Perform search with specific filter (for immediate city filter updates)
  const performSearchWithFilter = async (filter: { name?: string; code?: string; city?: string }, page?: number, size?: number) => {
    setSearchLoading(true);
    setError(null);
    try {
      // Use provided page/size or current state values
      const searchPage = page !== undefined ? page : currentPage;
      const searchSize = size !== undefined ? size : pageSize;
      
      const res = await stationAPI.searchStations(filter, searchPage, searchSize);
      console.log('Search API response:', res);
      
      if (res.status >= 200 && res.status < 300) {
        const pageResponse = res.data;
        safeSetStations(pageResponse.content);
        setTotalElements(pageResponse.totalElements);
        setTotalPages(pageResponse.totalPages);
        setCurrentPage(pageResponse.pageNumber);
        setPageSize(pageResponse.pageSize);
      } else {
        console.error('Unexpected search response status:', res.status);
        setError(`Unexpected response status: ${res.status}`);
        setStations([]);
      }
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Error performing search:', error);
      setError(error.response?.data?.message || error.message || 'Failed to perform search');
      setStations([]);
    } finally {
      setSearchLoading(false);
    }
  };
  
  // Handle individual filter changes with immediate search
  const handleFilterChangeWithSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    const value = e.target.value;
    setFilterForm({ ...filterForm, [name]: value });
    
    // For dropdown filters, search immediately and reset to first page
    if (name === 'isInterchange' || name === 'isActive' || name === 'status') {
      setCurrentPage(0);
      performSearch(0);
    }
  };
  
  // Handle text input changes with debounced search
  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e.target.name as string;
    const value = e.target.value;
    
    // Immediately update the input value for responsive UI
    setFilterForm({ ...filterForm, [name]: value });
    
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    debounceTimeoutRef.current = setTimeout(() => {
      // After debounce, call performSearch with current input values
      const currentFilter = {
        name: name === 'name' ? value : filterForm.name,
        code: name === 'code' ? value : filterForm.code,
        cityId: filterForm.cityId
      };
      
      // Convert to API format
      const simpleFilter = {
        name: currentFilter.name || undefined,
        code: currentFilter.code || undefined,
        cityId: currentFilter.cityId ? cities.find(c => c.id === currentFilter.cityId)?.id : undefined
      };
      
      performSearchWithFilter(simpleFilter);
    }, 300);
  };

  const fetchCities = async () => {
    setFetchingCities(true);
    try {
      const res = await cityAPI.getAll();
      console.log('Cities API response:', res);
      console.log('Cities data:', res.data);
      console.log('Cities data type:', typeof res.data);
      console.log('Is array:', Array.isArray(res.data));
      
      if (res.status >= 200 && res.status < 300) {
        safeSetCities(res.data);
      } else {
        console.error('Unexpected cities response status:', res.status);
        setFormError(`Unexpected response status: ${res.status}`);
        setCities([]);
      }
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Error fetching cities:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      if (error.response?.status === 401) {
        setFormError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        setFormError('Access denied. You do not have permission to view cities.');
      } else {
        setFormError(error.response?.data?.message || error.message || 'Failed to fetch cities');
      }
      setCities([]);
    } finally {
      setFetchingCities(false);
    }
  };

  const openAddModal = async () => {
    setIsEditMode(false);
    setEditingStationId(null);
    setShowModal(true);
    setForm({ 
      name: '', 
      stationCode: '', 
      cityId: '', 
      isInterchange: false, 
      lat: '', 
      lng: '', 
      description: '', 
      firstTrainTime: '', 
      lastTrainTime: '', 
      isActive: true, 
      status: 'OPERATIONAL',
      structures: [],
      totalPlatforms: '',
      totalTracks: ''
    });
    setFormError('');
    await fetchCities();
  };

  const openEditModal = async (station: Station) => {
    setIsEditMode(true);
    setEditingStationId(station.id!);
    setShowModal(true);
    setForm({
      name: station.name,
      stationCode: station.stationCode,
      cityId: station.cityId,
      isInterchange: station.isInterchange || false,
      lat: station.lat.toString(),
      lng: station.lng.toString(),
      description: station.description || '',
      firstTrainTime: station.firstTrainTime || '',
      lastTrainTime: station.lastTrainTime || '',
      isActive: station.isActive || true,
      status: station.status || 'OPERATIONAL',
      structures: station.structures || [],
      totalPlatforms: station.totalPlatforms?.toString() || '',
      totalTracks: station.totalTracks?.toString() || ''
    });
    setFormError('');
    await fetchCities();
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditingStationId(null);
    setForm({ 
      name: '', 
      stationCode: '', 
      cityId: '', 
      isInterchange: false, 
      lat: '', 
      lng: '', 
      description: '', 
      firstTrainTime: '', 
      lastTrainTime: '', 
      isActive: true, 
      status: 'OPERATIONAL',
      structures: [],
      totalPlatforms: '',
      totalTracks: ''
    });
    setFormError('');
  };

  const handleTextFieldInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError('');
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    setForm({ ...form, [name]: e.target.value });
    setFormError('');
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.checked });
  };

  const handleSaveStation = async () => {
    if (!form.name || !form.stationCode || !form.cityId) {
      setFormError('Name, Station Code, and City are required');
      return;
    }

    setSaving(true);
    try {
      const stationData = {
        ...form,
        lat: form.lat ? parseFloat(form.lat) : 0,
        lng: form.lng ? parseFloat(form.lng) : 0,
        totalPlatforms: form.totalPlatforms ? parseInt(form.totalPlatforms) : undefined,
        totalTracks: form.totalTracks ? parseInt(form.totalTracks) : undefined
      };

      if (isEditMode && editingStationId) {
        await stationAPI.update(editingStationId, stationData);
        setSuccessMsg('Station updated successfully');
      } else {
        await stationAPI.create(stationData);
        setSuccessMsg('Station created successfully');
      }
      await fetchStations();
      closeModal();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to save station');
    } finally {
      setSaving(false);
    }
  };

  const handleExportStations = async () => {
    try {
      // Get all stations without pagination for export
      const simpleFilter = getSimpleFilter();
      const response = await stationAPI.searchStations(simpleFilter, 0, 1000);
      const allStations = response.data.content;
      
      if (!Array.isArray(allStations) || allStations.length === 0) {
        setError('No stations available for export');
        return;
      }

      // Prepare data for export
      const exportData = allStations.map(station => ({
        'Station Name': station.name || '',
        'Station Code': station.stationCode || '',
        'City ID': station.cityId || '',
        'City Name': station.cityName || '',
        'Is Interchange': station.isInterchange ? 'Yes' : 'No',
        'Latitude': station.lat || '',
        'Longitude': station.lng || '',
        'Description': station.description || '',
        'First Train Time': station.firstTrainTime || '',
        'Last Train Time': station.lastTrainTime || '',
        'Is Active': station.isActive ? 'Yes' : 'No',
        'Status': station.status || '',
        'Structures': station.structures ? station.structures.join(', ') : '',
        'Total Platforms': station.totalPlatforms || '',
        'Total Tracks': station.totalTracks || ''
      }));

      // Create Excel workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths for better readability
      const columnWidths = [
        { wch: 20 }, // Station Name
        { wch: 15 }, // Station Code
        { wch: 15 }, // City ID
        { wch: 20 }, // City Name
        { wch: 15 }, // Is Interchange
        { wch: 12 }, // Latitude
        { wch: 12 }, // Longitude
        { wch: 30 }, // Description
        { wch: 15 }, // First Train Time
        { wch: 15 }, // Last Train Time
        { wch: 12 }, // Is Active
        { wch: 15 }, // Status
        { wch: 25 }, // Structures
        { wch: 15 }, // Total Platforms
        { wch: 15 }  // Total Tracks
      ];
      worksheet['!cols'] = columnWidths;

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Stations');

      // Generate Excel file and download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `stations_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccessMsg(`Successfully exported ${allStations.length} stations to Excel`);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to export stations');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this station?')) return;
    
    setDeletingId(id);
    try {
      await stationAPI.delete(id);
      setSuccessMsg('Station deleted successfully');
      await fetchStations();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete station');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Stations Management
        </Typography>
        {searchLoading && (
          <Chip
            icon={<CircularProgress size={16} />}
            label="Searching..."
            color="primary"
            variant="outlined"
          />
        )}
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchStations}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setShowImportModal(true)}
            sx={{ borderRadius: 2 }}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportStations}
            disabled={loading || !Array.isArray(stations) || stations.length === 0}
            sx={{ borderRadius: 2 }}
          >
            Export
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={openAddModal}
            sx={{ borderRadius: 2 }}
          >
            Add Station
          </Button>
        </Box>
      </Box>

      {/* Filter Section */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filter Stations
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Station Name"
                  name="name"
                  value={filterForm.name}
                  onChange={handleTextInputChange}
                  fullWidth
                  size="small"
                  placeholder="Search by station name..."
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                    endAdornment: searchLoading && (
                      <CircularProgress size={20} />
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Station Code"
                  name="code"
                  value={filterForm.code}
                  onChange={handleTextInputChange}
                  fullWidth
                  size="small"
                  placeholder="Search by station code..."
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                    endAdornment: searchLoading && (
                      <CircularProgress size={20} />
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={cities}
                  getOptionLabel={(option) => option.name}
                  value={cities.find(city => city.id === filterForm.cityId) || null}
                  onChange={(event, newValue) => {
                    const cityId = newValue?.id || '';
                    setFilterForm({ ...filterForm, cityId });
                    if (newValue) {
                      // Use the new value directly instead of waiting for state update
                      const tempFilter = { ...filterForm, cityId };
                      const simpleFilter = {
                        name: tempFilter.name || undefined,
                        code: tempFilter.code || undefined,
                        cityId: newValue.id
                      };
                      performSearchWithFilter(simpleFilter);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      placeholder="Select city..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {option.country}
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Interchange</InputLabel>
                  <Select
                    name="isInterchange"
                    value={filterForm.isInterchange}
                    onChange={handleFilterChangeWithSearch}
                    label="Interchange"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Active</InputLabel>
                  <Select
                    name="isActive"
                    value={filterForm.isActive}
                    onChange={handleFilterChangeWithSearch}
                    label="Active"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={filterForm.status}
                    onChange={handleFilterChangeWithSearch}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="OPERATIONAL">OPERATIONAL</MenuItem>
                    <MenuItem value="MAINTENANCE">MAINTENANCE</MenuItem>
                    <MenuItem value="CLOSED">CLOSED</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    startIcon={<Clear />}
                    fullWidth
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => setError(null)}
          action={
            <Button color="inherit" size="small" onClick={fetchStations}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Interchange</TableCell>
              <TableCell>Latitude</TableCell>
              <TableCell>Longitude</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>First Train</TableCell>
              <TableCell>Last Train</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Platforms</TableCell>
              <TableCell>Tracks</TableCell>
              <TableCell>Structures</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
              </TableHead>
              <TableBody>
                {!Array.isArray(stations) ? (
                                  <TableRow>
                  <TableCell colSpan={16} align="center">Error: Invalid data format</TableCell>
                </TableRow>
              ) : stations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={16} align="center">No stations found.</TableCell>
                </TableRow>
                ) : stations.map(station => (
                  <TableRow key={station.id} hover>
                    <TableCell>{station.name}</TableCell>
                    <TableCell>{station.stationCode}</TableCell>
                    <TableCell>{station.cityName || station.cityId}</TableCell>
                    <TableCell>
                      <Chip 
                        label={station.isInterchange ? 'Yes' : 'No'} 
                        color={station.isInterchange ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{station.lat}</TableCell>
                    <TableCell>{station.lng}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {station.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>{station.firstTrainTime || 'N/A'}</TableCell>
                    <TableCell>{station.lastTrainTime || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={station.status || 'OPERATIONAL'} 
                        color={station.status === 'OPERATIONAL' ? 'success' : station.status === 'MAINTENANCE' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={station.isActive ? 'Yes' : 'No'} 
                        color={station.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {station.totalPlatforms || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {station.totalTracks || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {station.structures && station.structures.length > 0 ? (
                        <Box>
                          {station.structures.slice(0, 2).map((structure, index) => (
                            <Chip 
                              key={index} 
                              label={structure} 
                              size="small" 
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                          {station.structures.length > 2 && (
                            <Chip 
                              label={`+${station.structures.length - 2} more`} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </Box>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditModal(station)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(station.id!)}
                            disabled={deletingId === station.id}
                          >
                            {deletingId === station.id ? <CircularProgress size={16} /> : <Delete />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        {/* Pagination Controls */}
        {Array.isArray(stations) && stations.length > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} p={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="text.secondary">
                Showing {stations.length} of {totalElements} stations
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    const newPageSize = Number(e.target.value);
                    setPageSize(newPageSize);
                    setCurrentPage(0);
                    performSearch(0, newPageSize);
                  }}
                >
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={20}>20 per page</MenuItem>
                  <MenuItem value={50}>50 per page</MenuItem>
                  <MenuItem value={100}>100 per page</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setCurrentPage(0);
                  performSearch(0);
                }}
                disabled={currentPage === 0}
              >
                First
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const newPage = currentPage - 1;
                  setCurrentPage(newPage);
                  performSearch(newPage);
                }}
                disabled={currentPage === 0}
              >
                Previous
              </Button>
              <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                Page {currentPage + 1} of {totalPages}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const newPage = currentPage + 1;
                  setCurrentPage(newPage);
                  performSearch(newPage);
                }}
                disabled={currentPage >= totalPages - 1}
              >
                Next
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const newPage = totalPages - 1;
                  setCurrentPage(newPage);
                  performSearch(newPage);
                }}
                disabled={currentPage >= totalPages - 1}
              >
                Last
              </Button>
            </Box>
          </Box>
        )}
      </>
      )}
      <Dialog open={showModal} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Station' : 'Add New Station'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={fetchCities}>
                  Retry
                </Button>
              }
            >
              {formError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TranslationInput
                label="Name"
                value={form.name}
                onChange={(value) => setForm({ ...form, name: value })}
                entityType="STATION"
                entityId={isEditMode && editingStationId ? editingStationId : 'new'}
                fieldName="name"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Station Code" name="stationCode" value={form.stationCode} onChange={handleTextFieldInput} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>City</InputLabel>
                <Select
                  name="cityId"
                  value={form.cityId}
                  onChange={handleSelectInput}
                  label="City"
                  disabled={fetchingCities}
                >
                  <MenuItem value="">Select a city</MenuItem>
                  {cities.map(city => (
                    <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                  ))}
                </Select>
                {fetchingCities && <CircularProgress size={20} sx={{ position: 'absolute', right: 10, top: 10 }} />}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox name="isInterchange" checked={form.isInterchange} onChange={handleCheckbox} />}
                label="Interchange"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Latitude" name="lat" value={form.lat} onChange={handleTextFieldInput} fullWidth required type="number" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Longitude" name="lng" value={form.lng} onChange={handleTextFieldInput} fullWidth required type="number" />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Description" 
                name="description" 
                value={form.description} 
                onChange={handleTextFieldInput} 
                fullWidth 
                multiline 
                rows={3}
                placeholder="Brief description of the station (optional)"
                helperText="Maximum 500 characters"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="First Train Time" name="firstTrainTime" value={form.firstTrainTime} onChange={handleTextFieldInput} fullWidth placeholder="06:00" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last Train Time" name="lastTrainTime" value={form.lastTrainTime} onChange={handleTextFieldInput} fullWidth placeholder="23:30" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={form.status}
                  onChange={handleSelectInput}
                  label="Status"
                >
                  <MenuItem value="OPERATIONAL">OPERATIONAL</MenuItem>
                  <MenuItem value="MAINTENANCE">MAINTENANCE</MenuItem>
                  <MenuItem value="CLOSED">CLOSED</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox name="isActive" checked={form.isActive} onChange={handleCheckbox} />}
                label="Active"
              />
            </Grid>
            
            {/* New fields for station capacity and structures */}
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Total Platforms" 
                name="totalPlatforms" 
                value={form.totalPlatforms} 
                onChange={handleTextFieldInput} 
                fullWidth 
                type="number"
                inputProps={{ min: 1 }}
                placeholder="4"
                helperText="Number of platforms"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Total Tracks" 
                name="totalTracks" 
                value={form.totalTracks} 
                onChange={handleTextFieldInput} 
                fullWidth 
                type="number"
                inputProps={{ min: 1 }}
                placeholder="6"
                helperText="Number of tracks"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Structures & Facilities" 
                name="structures" 
                value={form.structures.join(', ')} 
                onChange={(e) => {
                  const structuresArray = e.target.value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                  setForm({ ...form, structures: structuresArray });
                }}
                fullWidth 
                placeholder="Elevator, Escalator, Parking, ATM"
                helperText="Comma-separated list of station structures and facilities"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveStation} variant="contained" color="primary" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (isEditMode ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Modal */}
      <StationImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => {
          setShowImportModal(false);
          fetchStations();
          setSuccessMsg('Stations imported successfully');
        }}
      />
    </Box>
  );
};

export default Stations; 